/**
 * Navigation Module
 *
 * Handles mobile menu toggle, keyboard navigation,
 * focus trapping for accessibility, and mobile sliding panels (3 levels).
 */

const MOBILE_NAV_MAX_WIDTH = 1200;
let level3TriggerId = 0;

function isMobileNav() {
    return window.innerWidth <= MOBILE_NAV_MAX_WIDTH;
}

/**
 * Ollie Menu Designer applies desktop left/width inline (≥ 600px).
 * Reset on mobile so panels align to the viewport.
 * @param {HTMLElement} container
 */
function resetMegaMenuMobileLayout(container) {
    if (!isMobileNav() || !container) return;

    container.style.left = '0';
    container.style.right = '0';
    container.style.width = '100%';
    container.style.maxWidth = '100%';
    container.style.marginLeft = '0';
}

/**
 * Force level-2 submenus open inside top-level navigation panels (desktop WP nav).
 */
function initMegaMenu() {
    const topItems = document.querySelectorAll(
        '.wp-block-navigation__container > .wp-block-navigation-item.has-child'
    );

    topItems.forEach((topItem) => {
        const panel = topItem.querySelector(':scope > .wp-block-navigation__submenu-container');
        if (!panel) return;

        panel.querySelectorAll(':scope > li').forEach((li) => {
            const sub = li.querySelector(':scope > .wp-block-navigation__submenu-container');
            if (!sub) return;

            try {
                const ctx = JSON.parse(li.getAttribute('data-wp-context') || '{}');
                ctx.submenuOpenedBy = { click: true, hover: false, focus: false };
                li.setAttribute('data-wp-context', JSON.stringify(ctx));
            } catch (e) {
                // Ignore invalid JSON context.
            }

            sub.style.cssText =
                'position:static!important;display:block!important;' +
                'visibility:visible!important;opacity:1!important;' +
                'pointer-events:all!important;box-shadow:none!important;' +
                'transform:none!important;';
        });
    });
}

/**
 * Group level-3 ghost buttons under their level-2 fill trigger.
 * @param {HTMLElement} buttonsContainer
 * @returns {{ trigger: HTMLElement, items: HTMLElement[] }[]}
 */
function getLevel3Groups(buttonsContainer) {
    const groups = [];
    let current = null;

    buttonsContainer.querySelectorAll(':scope > .wp-block-button').forEach((btn) => {
        if (btn.classList.contains('is-style-fill')) {
            if (current?.items.length) {
                groups.push(current);
            }
            current = { trigger: btn, items: [] };
        } else if (btn.classList.contains('is-style-hf-ghost') && current) {
            current.items.push(btn);
        }
    });

    if (current?.items.length) {
        groups.push(current);
    }

    return groups;
}

/**
 * Ensure level-2 scrollable body wrapper exists inside a mega menu panel.
 * @param {HTMLElement} dropdown
 * @returns {HTMLElement}
 */
function ensureLevel2Body(dropdown) {
    let body = dropdown.querySelector(':scope > .cdg-panel-level2-body');
    if (body) return body;

    body = document.createElement('div');
    body.className = 'cdg-panel-level2-body';

    const header = dropdown.querySelector(':scope > .cdg-panel-header');
    const children = [...dropdown.children].filter(
        (el) => el !== header && !el.classList.contains('cdg-level3-panel')
    );

    children.forEach((el) => body.appendChild(el));
    dropdown.insertBefore(body, header ? header.nextSibling : dropdown.firstChild);

    return body;
}

/**
 * Inject mobile back-header into a level-2 dropdown (once).
 * @param {HTMLElement} dropdown
 * @param {HTMLElement|null} navList
 */
function setupMobileHeader(dropdown, navList) {
    if (dropdown.querySelector(':scope > .cdg-panel-header')) return;

    const toggleId = dropdown.getAttribute('aria-labelledby');
    const toggle = toggleId ? document.getElementById(toggleId) : null;
    const label = toggle?.querySelector('.wp-block-navigation-item__label')?.textContent || '';

    const header = document.createElement('div');
    header.className = 'cdg-panel-header';
    header.innerHTML = `
        <button type="button" class="cdg-panel-back" aria-label="Retour au menu"></button>
        <span class="cdg-panel-title"></span>
    `;
    header.querySelector('.cdg-panel-title').textContent = label;
    dropdown.insertBefore(header, dropdown.firstChild);

    header.querySelector('.cdg-panel-back').addEventListener('click', () => {
        closeLevel2Panel(dropdown, navList);
    });
}

/**
 * Restore desktop DOM — undo all mobile-only mutations.
 * @param {HTMLElement} dropdown
 */
function teardownMobileMenuDOM(dropdown) {
    const level2Body = dropdown.querySelector(':scope > .cdg-panel-level2-body');

    if (level2Body) {
        dropdown.querySelectorAll(':scope > .cdg-level3-panel.cdg-panel-open').forEach((panel) => {
            closeLevel3Panel(panel, level2Body);
        });
    }

    dropdown.querySelectorAll(':scope > .cdg-level3-panel').forEach((panel) => {
        const trigger = panel.dataset.cdgTriggerId
            ? document.getElementById(panel.dataset.cdgTriggerId)
            : null;
        const list = panel.querySelector('.cdg-level3-list');

        if (trigger && list) {
            let anchor = trigger;
            [...list.children].forEach((item) => {
                anchor.insertAdjacentElement('afterend', item);
                anchor = item;
            });
        }

        panel.remove();
    });

    dropdown.querySelectorAll('.cdg-has-level3').forEach((el) => {
        el.classList.remove('cdg-has-level3');
    });

    if (level2Body) {
        while (level2Body.firstChild) {
            dropdown.insertBefore(level2Body.firstChild, level2Body);
        }
        level2Body.remove();
    }

    dropdown.querySelector(':scope > .cdg-panel-header')?.remove();

    dropdown.classList.remove('cdg-panel-open');
    dropdown.style.left = '';
    dropdown.style.right = '';
    dropdown.style.width = '';
    dropdown.style.maxWidth = '';
    dropdown.style.marginLeft = '';

    const toggleId = dropdown.getAttribute('aria-labelledby');
    document.getElementById(toggleId)?.setAttribute('aria-expanded', 'false');

    delete dropdown.dataset.cdgLevel3Setup;
}

/**
 * Apply or remove mobile DOM depending on viewport width.
 */
function syncMobileMenuDOM() {
    const navList = document.querySelector('ul.wp-block-navigation__container');

    document.querySelectorAll('.wp-block-ollie-mega-menu').forEach((item) => {
        const dropdown = item.querySelector('.wp-block-ollie-mega-menu__menu-container');
        if (!dropdown) return;

        if (isMobileNav()) {
            setupLevel3Panels(dropdown);
            setupMobileHeader(dropdown, navList);
        } else {
            teardownMobileMenuDOM(dropdown);
        }
    });

    if (!isMobileNav()) {
        navList?.classList.remove('cdg-panel-hidden');
    }
}

/**
 * Build level-3 sliding panels inside a level-2 mega menu dropdown.
 * Mobile only — call via syncMobileMenuDOM().
 * @param {HTMLElement} dropdown
 */
function setupLevel3Panels(dropdown) {
    if (!isMobileNav()) return;
    if (dropdown.dataset.cdgLevel3Setup === 'true') return;
    dropdown.dataset.cdgLevel3Setup = 'true';

    const level2Body = ensureLevel2Body(dropdown);

    level2Body.querySelectorAll('.wp-block-buttons').forEach((buttonsContainer) => {
        getLevel3Groups(buttonsContainer).forEach(({ trigger, items }) => {
            trigger.classList.add('cdg-has-level3');

            const label =
                trigger.querySelector('.wp-block-button__link')?.textContent?.trim() || '';

            const panel = document.createElement('div');
            panel.className = 'cdg-level3-panel';
            panel.setAttribute('aria-hidden', 'true');
            panel.innerHTML = `
                <div class="cdg-panel-header">
                    <button type="button" class="cdg-panel-back" aria-label="Retour"></button>
                    <span class="cdg-panel-title"></span>
                </div>
                <div class="cdg-level3-list"></div>
            `;

            panel.querySelector('.cdg-panel-title').textContent = label;

            const list = panel.querySelector('.cdg-level3-list');
            items.forEach((item) => list.appendChild(item));

            dropdown.appendChild(panel);

            panel.querySelector('.cdg-panel-back').addEventListener('click', () => {
                closeLevel3Panel(panel, level2Body);
            });

            const link = trigger.querySelector('.wp-block-button__link');
            if (!link) return;

            if (!link.id) {
                level3TriggerId += 1;
                link.id = `cdg-l3-trigger-${level3TriggerId}`;
            }
            panel.dataset.cdgTriggerId = link.id;

            link.addEventListener(
                'click',
                (e) => {
                    if (!isMobileNav()) return;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    openLevel3Panel(panel, level2Body);
                },
                true
            );
        });
    });
}

/**
 * Initialize sliding panel navigation for mobile (≤ 1200px).
 * Level 1 → Level 2 → Level 3, each sliding in from the right.
 */
function initMobileSlidingPanels() {
    const navList = document.querySelector('ul.wp-block-navigation__container');
    const items   = document.querySelectorAll('.wp-block-ollie-mega-menu');

    syncMobileMenuDOM();

    items.forEach((item) => {
        const toggle   = item.querySelector('.wp-block-ollie-mega-menu__toggle');
        const dropdown = item.querySelector('.wp-block-ollie-mega-menu__menu-container');
        if (!toggle || !dropdown) return;

        if (toggle.dataset.cdgPanelBound === 'true') return;
        toggle.dataset.cdgPanelBound = 'true';

        toggle.addEventListener(
            'click',
            (e) => {
                if (!isMobileNav()) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                openLevel2Panel(dropdown, navList);
            },
            true
        );
    });

    const mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_NAV_MAX_WIDTH}px)`);
    mobileMediaQuery.addEventListener('change', () => {
        resetSlidingPanels();
        syncMobileMenuDOM();
    });

    const responsiveNav = document.querySelector('.wp-block-navigation__responsive-container');
    if (responsiveNav && !responsiveNav.dataset.cdgNavResetBound) {
        responsiveNav.dataset.cdgNavResetBound = 'true';
        responsiveNav.addEventListener('click', (e) => {
            if (e.target.closest('.wp-block-navigation__responsive-container-close')) {
                resetSlidingPanels();
            }
        });
    }

    if (!document.documentElement.dataset.cdgPanelEscapeBound) {
        document.documentElement.dataset.cdgPanelEscapeBound = 'true';
        document.addEventListener('keydown', (e) => {
            if (!isMobileNav() || e.key !== 'Escape') return;

            const navList = document.querySelector('ul.wp-block-navigation__container');
            const openL3 = document.querySelector('.cdg-level3-panel.cdg-panel-open');

            if (openL3) {
                const dropdown = openL3.closest('.wp-block-ollie-mega-menu__menu-container');
                const level2Body = dropdown?.querySelector(':scope > .cdg-panel-level2-body');
                if (level2Body) closeLevel3Panel(openL3, level2Body);
                return;
            }

            const openL2 = document.querySelector(
                '.wp-block-ollie-mega-menu__menu-container.cdg-panel-open'
            );
            if (openL2) closeLevel2Panel(openL2, navList);
        });
    }
}

/**
 * Open level-2 panel (mega menu dropdown).
 * @param {HTMLElement} panel
 * @param {HTMLElement|null} navList
 */
function openLevel2Panel(panel, navList) {
    document
        .querySelectorAll('.wp-block-ollie-mega-menu__menu-container.cdg-panel-open')
        .forEach((openPanel) => {
            if (openPanel !== panel) closeLevel2Panel(openPanel, navList);
        });

    closeAllLevel3In(panel);
    resetMegaMenuMobileLayout(panel);
    panel.classList.add('cdg-panel-open');
    navList?.classList.add('cdg-panel-hidden');

    const toggleId = panel.getAttribute('aria-labelledby');
    const toggle = toggleId ? document.getElementById(toggleId) : null;
    toggle?.setAttribute('aria-expanded', 'true');

    panel.querySelector('.cdg-panel-back')?.focus();
}

/**
 * Close level-2 panel and restore level-1 list.
 * @param {HTMLElement} panel
 * @param {HTMLElement|null} navList
 */
function closeLevel2Panel(panel, navList) {
    closeAllLevel3In(panel);
    panel.classList.remove('cdg-panel-open');
    navList?.classList.remove('cdg-panel-hidden');

    const id = panel.getAttribute('aria-labelledby');
    const trigger = id ? document.getElementById(id) : null;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
}

/**
 * Open level-3 panel inside an open level-2 panel.
 * @param {HTMLElement} panel
 * @param {HTMLElement} level2Body
 */
function openLevel3Panel(panel, level2Body) {
    const dropdown = level2Body.closest('.wp-block-ollie-mega-menu__menu-container');
    if (dropdown) {
        closeAllLevel3In(dropdown);
        resetMegaMenuMobileLayout(dropdown);
        // Ollie peut réappliquer left/width après resize — forcer après layout
        requestAnimationFrame(() => resetMegaMenuMobileLayout(dropdown));
    }

    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('cdg-panel-open');
    level2Body.classList.add('cdg-panel-hidden');
    panel.querySelector('.cdg-panel-back')?.focus();
}

/**
 * Close level-3 panel and restore level-2 body.
 * @param {HTMLElement} panel
 * @param {HTMLElement} level2Body
 */
function closeLevel3Panel(panel, level2Body) {
    panel.classList.remove('cdg-panel-open');
    panel.setAttribute('aria-hidden', 'true');
    level2Body.classList.remove('cdg-panel-hidden');

    const trigger = panel.dataset.cdgTriggerId
        ? document.getElementById(panel.dataset.cdgTriggerId)
        : null;
    trigger?.focus();
}

/**
 * Close every open level-3 panel within a level-2 dropdown.
 * @param {HTMLElement} level2Panel
 */
function closeAllLevel3In(level2Panel) {
    const level2Body = level2Panel.querySelector(':scope > .cdg-panel-level2-body');
    if (!level2Body) return;

    level2Panel.querySelectorAll(':scope > .cdg-level3-panel.cdg-panel-open').forEach((panel) => {
        closeLevel3Panel(panel, level2Body);
    });
}

/**
 * Reset all sliding panel state (e.g. on resize to desktop).
 */
function resetSlidingPanels() {
    if (!isMobileNav()) return;

    const navList = document.querySelector('ul.wp-block-navigation__container');

    document
        .querySelectorAll('.wp-block-ollie-mega-menu__menu-container.cdg-panel-open')
        .forEach((panel) => closeLevel2Panel(panel, navList));
}

/**
 * Initialize navigation functionality.
 */
export function initNavigation() {
    initMegaMenu();
    initMobileSlidingPanels();

    const menuToggle = document.querySelector('.hf-menu-toggle');
    const mobileMenu = document.querySelector('.hf-mobile-menu');

    if (!menuToggle || !mobileMenu) {
        return;
    }

    const focusableElements =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    function toggleMenu() {
        const isOpen = mobileMenu.classList.contains('is-open');

        mobileMenu.classList.toggle('is-open');
        menuToggle.setAttribute('aria-expanded', String(!isOpen));
        document.body.classList.toggle('menu-open', !isOpen);

        if (!isOpen) {
            const firstFocusable = mobileMenu.querySelector(focusableElements);
            firstFocusable?.focus();
            document.body.style.overflow = 'hidden';
        } else {
            resetSlidingPanels();
            menuToggle.focus();
            document.body.style.overflow = '';
        }
    }

    function handleKeydown(event) {
        if (event.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
            const openL3 = document.querySelector('.cdg-level3-panel.cdg-panel-open');
            const openL2 = document.querySelector(
                '.wp-block-ollie-mega-menu__menu-container.cdg-panel-open'
            );
            if (!openL3 && !openL2) {
                toggleMenu();
            }
        }
    }

    function handleClickOutside(event) {
        if (
            mobileMenu.classList.contains('is-open') &&
            !mobileMenu.contains(event.target) &&
            !menuToggle.contains(event.target)
        ) {
            toggleMenu();
        }
    }

    menuToggle.addEventListener('click', toggleMenu);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('click', handleClickOutside);

    const mediaQuery = window.matchMedia(`(min-width: ${MOBILE_NAV_MAX_WIDTH + 1}px)`);
    mediaQuery.addEventListener('change', (e) => {
        if (e.matches) {
            syncMobileMenuDOM();
            if (mobileMenu.classList.contains('is-open')) {
                toggleMenu();
            }
        }
    });
}
